import { DragDropModule } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';

import { AlertingCardTableSortChangeEvent } from '@shared/components/alerting-card-table';
import { DepotOverviewCardComponent } from '@shared/components/depot-overview-card';
import { DepotOverviewCardLoadingSkeletonComponent } from '@shared/components/depot-overview-card-loading-skeleton';
import { LoadingSkeletonComponent } from '@shared/components/loading-skeleton';
import {
  MovableCardDirective,
  MovableCardsContainerComponent,
  MovableCardsContainerViewModel,
} from '@shared/components/movable-cards-container';
import { TenantQuickSelectorComponent } from '@shared/components/tenant-quick-selector';

import { appDrag32Icon } from '@icons/drag-32';

import { CustomTitleTemplateDirective } from '../layout/directives/custom-title-template.directive';
import { ChargersOverviewMovablePanelComponent } from './components/chargers-overview-movable-panel';
import { EnergyCostOverviewMovablePanelComponent } from './components/energy-cost-overview-movable-panel';
import { SiteAlertsMovablePanelComponent } from './components/site-alerts-movable-panel';
import { SiteFleetsOverviewMovablePanelComponent } from './components/site-fleets-overview-movable-panel';
import { VehicleStateOfChargeSummaryMovablePanelComponent } from './components/vehicle-state-of-charge-summary-movable-panel';
import { EnergyCostRange } from './enums/energy-cost-range.enum';
import { MultiSiteDashboardCard } from './enums/multi-site-dashboard-card.enum';
import { VehicleSocSummaryRange } from './enums/vehicle-soc-summary-range.enum';
import { MultiSiteDashboardService } from './multi-site-dashboard.service';

@Component({
  selector: 'app-multi-site-dashboard',
  imports: [
    CustomTitleTemplateDirective,
    TenantQuickSelectorComponent,
    LoadingSkeletonComponent,
    DepotOverviewCardComponent,
    DepotOverviewCardLoadingSkeletonComponent,
    MovableCardsContainerComponent,
    MovableCardDirective,
    SiteFleetsOverviewMovablePanelComponent,
    EnergyCostOverviewMovablePanelComponent,
    ChargersOverviewMovablePanelComponent,
    SiteAlertsMovablePanelComponent,
    VehicleStateOfChargeSummaryMovablePanelComponent,
    DragDropModule,
    SvgIconComponent,
  ],
  templateUrl: './multi-site-dashboard.component.html',
  styleUrl: './multi-site-dashboard.component.scss',
  providers: [MultiSiteDashboardService],
  host: { class: 'flex flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSiteDashboardComponent {
  protected readonly multiSiteDashboardService = inject(MultiSiteDashboardService);

  protected readonly tenantsAreLoading = this.multiSiteDashboardService.tenantsAreLoading;
  protected readonly selectedTenantId = computed(() => this.multiSiteDashboardService.selectedTenant()?.id);
  protected readonly hasMultipleTenants = this.multiSiteDashboardService.hasMultipleTenants;
  protected readonly selectedTenantName = this.multiSiteDashboardService.selectedTenantName;
  protected readonly depotOverviewCardVm = this.multiSiteDashboardService.depotOverviewCardVm;
  protected readonly depotOverviewCardIsLoading = this.multiSiteDashboardService.depotOverviewCardIsLoading;
  protected readonly cardsLayout = this.multiSiteDashboardService.cardsLayout;
  protected readonly siteFleetsOverviewVm = this.multiSiteDashboardService.siteFleetsOverviewVm;
  protected readonly siteFleetsOverviewIsLoading = this.multiSiteDashboardService.siteFleetsOverviewIsLoading;
  protected readonly chargersOverviewVm = this.multiSiteDashboardService.chargersOverviewVm;
  protected readonly chargersOverviewIsLoading = this.multiSiteDashboardService.chargersOverviewIsLoading;
  protected readonly siteAlertsVm = this.multiSiteDashboardService.siteAlertsVm;
  protected readonly siteAlertsIsLoading = this.multiSiteDashboardService.siteAlertsIsLoading;
  protected readonly energyCostOverviewVm = this.multiSiteDashboardService.energyCostOverviewVm;
  protected readonly energyCostOverviewIsLoading = this.multiSiteDashboardService.energyCostOverviewIsLoading;
  protected readonly energyCostRange = this.multiSiteDashboardService.energyCostRange;
  protected readonly vehicleSocSummaryVm = this.multiSiteDashboardService.vehicleSocSummaryVm;
  protected readonly vehicleSocSummaryIsLoading = this.multiSiteDashboardService.vehicleSocSummaryIsLoading;
  protected readonly vehicleSocSummaryRange = this.multiSiteDashboardService.vehicleSocSummaryRange;

  protected readonly MultiSiteDashboardCard = MultiSiteDashboardCard;

  // Show "Dashboard - <tenant>" in the header only when more than one tenant is available.
  protected readonly headerTitleSuffix = computed(() =>
    this.hasMultipleTenants() && this.selectedTenantName() ? ` - ${this.selectedTenantName()}` : ''
  );

  constructor() {
    injectRegisterIcons([appDrag32Icon]);
  }

  public onCardsLayoutChange(layout: MovableCardsContainerViewModel<MultiSiteDashboardCard>): void {
    this.multiSiteDashboardService.onCardsLayoutChange(layout);
  }

  public onTenantChange(tenantId: string): void {
    this.multiSiteDashboardService.selectTenant(tenantId);
  }

  public onSiteAlertsSortChange(event: AlertingCardTableSortChangeEvent): void {
    this.multiSiteDashboardService.setSiteAlertsSort(event.sortProperty, event.sortDirection);
  }

  public onEnergyCostRangeChange(range: EnergyCostRange): void {
    this.multiSiteDashboardService.setEnergyCostRange(range);
  }

  public onVehicleSocSummaryRangeChange(range: VehicleSocSummaryRange): void {
    this.multiSiteDashboardService.setVehicleSocSummaryRange(range);
  }
}
